/**
 * Admin Analytics Dashboard — `/admin/analytics`.
 *
 * v2: adds period-over-period KPI deltas, conversion funnel, UTM
 * source/campaign breakdown, conversion rates, revenue in period,
 * and a toggle to include/exclude staff (admin/employee) traffic.
 */

import { useEffect, useState, useMemo } from "react";
import {
  Eye, Users, MousePointerClick, Activity, Clock, Globe,
  Smartphone, Monitor, TrendingUp, TrendingDown, Minus,
  ArrowDownRight, Filter, Wallet, Zap, Tag, Megaphone,
  PauseCircle, PlayCircle, Radio, UserPlus, UserCheck,
  Repeat, LogIn, Flame,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const RANGES = [
  { d: 1,   label: "Hoje" },
  { d: 7,   label: "7 dias" },
  { d: 30,  label: "30 dias" },
  { d: 90,  label: "90 dias" },
  { d: 365, label: "1 ano" },
];

function fmtN(n) { return new Intl.NumberFormat("pt-PT").format(n || 0); }
function fmtEur(n) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n || 0);
}

function DeltaPill({ value }) {
  if (value === null || value === undefined) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-[#7c8e8b]"><Minus size={10}/> —</span>;
  }
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const cls = value > 0
    ? "text-emerald-300 bg-emerald-400/10"
    : value < 0 ? "text-red-300 bg-red-400/10" : "text-[#a0b2af] bg-[#1a4640]/40";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${cls}`}>
      <Icon size={9} /> {value > 0 ? "+" : ""}{value}%
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, hint, suffix = "", testId }) {
  return (
    <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#a0b2af] text-[10px] uppercase tracking-[0.18em]">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <div className="font-display text-3xl text-[#fdfbf7]">{typeof value === "string" ? value : fmtN(value)}{suffix}</div>
        <DeltaPill value={delta} />
      </div>
      {hint && <div className="mt-1 text-[10px] text-[#7c8e8b]">{hint}</div>}
    </div>
  );
}

function Bar({ value, max, tone = "gold" }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  const colors = {
    gold:    "bg-gradient-to-r from-[#d4af37]/40 to-[#d4af37]",
    blue:    "bg-gradient-to-r from-blue-500/30 to-blue-400",
    emerald: "bg-gradient-to-r from-emerald-500/30 to-emerald-400",
    rose:    "bg-gradient-to-r from-rose-500/30 to-rose-400",
  };
  return (
    <div className="h-1.5 w-full bg-[#1a4640]/50 rounded-full overflow-hidden">
      <div className={`h-full ${colors[tone] || colors.gold}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ListBlock({ icon: Icon, title, items, getKey, getValue, testId, tone, valueSuffix = "", empty = "Sem dados ainda." }) {
  const safeItems = Array.isArray(items) ? items : [];
  const max = safeItems.reduce((m, x) => Math.max(m, getValue(x)), 0);
  return (
    <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#fdfbf7] mb-4">
        {Icon && <Icon size={14} className="text-[#d4af37]" />}
        <h3 className="font-display text-sm">{title}</h3>
      </div>
      {safeItems.length === 0 ? (
        <p className="text-[12px] text-[#7c8e8b] italic">{empty}</p>
      ) : (
        <ul className="space-y-3">
          {safeItems.map((it, i) => (
            <li key={i} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2 text-[12px]">
                <span className="text-[#fdfbf7] truncate">{getKey(it)}</span>
                <span className="text-[#a0b2af] tabular-nums shrink-0">{fmtN(getValue(it))}{valueSuffix}</span>
              </div>
              <Bar value={getValue(it)} max={max} tone={tone} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Sparkline({ data, height = 80 }) {
  if (!data || data.length === 0) {
    return <div className="text-xs text-[#7c8e8b] italic">Sem dados.</div>;
  }
  const max = Math.max(1, ...data.map(d => d.pageviews));
  const w = 100, h = 100;
  const points = data.map((d, i) => {
    const x = (i / Math.max(1, data.length - 1)) * w;
    const y = h - (d.pageviews / max) * (h - 6) - 3;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#d4af37" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,100 ${points} 100,100`} fill="url(#spark)" stroke="none" />
      <polyline points={points} fill="none" stroke="#d4af37" strokeWidth="1.5" />
    </svg>
  );
}

function Heatmap({ data }) {
  // Build a 7 (dow Sun..Sat) × 24 (hour) matrix.
  const DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  let max = 0;
  (data || []).forEach(cell => {
    if (cell.dow >= 0 && cell.dow < 7 && cell.hour >= 0 && cell.hour < 24) {
      grid[cell.dow][cell.hour] = cell.pageviews;
      if (cell.pageviews > max) max = cell.pageviews;
    }
  });
  function bgFor(v) {
    if (!v) return "rgba(26, 70, 64, 0.4)";
    const pct = Math.min(1, v / Math.max(1, max));
    const alpha = 0.18 + pct * 0.82;
    return `rgba(212, 175, 55, ${alpha.toFixed(2)})`;
  }
  return (
    <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5 overflow-x-auto" data-testid="heatmap-block">
      <div className="flex items-center gap-2 text-[#fdfbf7] mb-4">
        <Flame size={14} className="text-[#d4af37]" />
        <h3 className="font-display text-sm">Mapa de calor — dia × hora</h3>
        <span className="text-[10px] text-[#7c8e8b] ml-auto">UTC</span>
      </div>
      <div className="inline-block min-w-full">
        <div className="grid grid-cols-[28px_repeat(24,_minmax(14px,_1fr))] gap-0.5 text-[9px]">
          <div></div>
          {Array.from({ length: 24 }, (_, h) => (
            <div key={`h-${h}`} className="text-center text-[#7c8e8b]">{h % 3 === 0 ? h : ""}</div>
          ))}
          {DOW.map((label, dow) => (
            <HeatRow key={`row-${dow}`} label={label} row={grid[dow]} bgFor={bgFor} />
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px] text-[#7c8e8b]">
        <span>Menos</span>
        <div className="flex items-center gap-0.5">
          {[0, 0.25, 0.5, 0.75, 1].map(p => (
            <div key={p} className="w-3.5 h-3.5 rounded-sm"
                 style={{ background: `rgba(212, 175, 55, ${(0.18 + p * 0.82).toFixed(2)})` }} />
          ))}
        </div>
        <span>Mais</span>
      </div>
    </div>
  );
}

function HeatRow({ label, row, bgFor }) {
  return (
    <>
      <div className="text-right pr-1 text-[#a0b2af] font-medium leading-[14px]">{label}</div>
      {row.map((v, h) => (
        <div key={`c-${h}`}
             title={`${label} ${String(h).padStart(2, "0")}:00 · ${v} views`}
             className="h-3.5 rounded-sm"
             style={{ background: bgFor(v) }} />
      ))}
    </>
  );
}

function Funnel({ steps }) {
  if (!steps || steps.length === 0) return null;
  const baseMax = Math.max(1, ...steps.map(s => s.count));
  return (
    <div className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5" data-testid="funnel-block">
      <div className="flex items-center gap-2 text-[#fdfbf7] mb-4">
        <Zap size={14} className="text-[#d4af37]" />
        <h3 className="font-display text-sm">Funil de conversão</h3>
        <span className="text-[10px] text-[#7c8e8b] ml-auto">Visitante → Pagamento</span>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => {
          const widthPct = Math.max(8, (s.count / baseMax) * 100);
          return (
            <div key={s.key} data-testid={`funnel-${s.key}`}>
              <div className="flex items-center justify-between text-[12px] mb-1">
                <span className="text-[#fdfbf7]">{i + 1}. {s.label}</span>
                <div className="flex items-center gap-3 text-[#a0b2af] tabular-nums shrink-0">
                  <span className="font-mono text-[#d4af37]">{fmtN(s.count)}</span>
                  <span className="text-[10px] w-12 text-right">{s.pct_of_visitors}%</span>
                  {s.pct_of_prev !== null && i > 0 && (
                    <span className="text-[10px] w-12 text-right text-emerald-300">↓{s.pct_of_prev}%</span>
                  )}
                </div>
              </div>
              <div className="h-6 rounded-md bg-[#1a4640]/50 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#a37c00]
                                flex items-center justify-end pr-2 text-[10px] text-[#041f1b] font-bold"
                     style={{ width: `${widthPct}%` }}>
                  {widthPct > 14 ? fmtN(s.count) : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[10px] text-[#7c8e8b]">
        % à direita: comparado com o visitante (1.º passo) · ↓ % é a conversão face ao passo anterior.
      </p>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [excludeInternal, setExcludeInternal] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [togglingFL, setTogglingFL] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/analytics/dashboard", {
      params: { days, exclude_internal: excludeInternal },
    })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => toast.error(formatApiError(e, "Falha ao carregar analytics")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days, excludeInternal]);

  useEffect(() => {
    api.get("/admin/settings")
      .then(r => setSettings(r.data))
      .catch(() => {});
  }, []);

  async function toggleFacturalusa() {
    if (!settings) return;
    const next = !settings.facturalusa_paused;
    setTogglingFL(true);
    try {
      const { data: s } = await api.patch("/admin/settings/facturalusa-paused", { paused: next });
      setSettings(s);
      toast.success(next
        ? "Emissão automática de faturas PAUSADA."
        : "Emissão automática de faturas REATIVADA.");
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível alterar a definição."));
    } finally {
      setTogglingFL(false);
    }
  }

  const t = data?.totals || {};
  const d = data?.deltas || {};
  const c = data?.conversion || {};
  const timeseries     = data?.timeseries     || [];
  const funnel         = data?.funnel         || [];
  const topPages       = data?.top_pages      || [];
  const topEvents      = data?.top_events     || [];
  const devices        = data?.devices        || [];
  const browsers       = data?.browsers       || [];
  const countries      = data?.countries      || [];
  const byRole         = data?.by_role        || [];
  const utmSources     = data?.utm_sources    || [];
  const utmMediums     = data?.utm_mediums    || [];
  const utmCampaigns   = data?.utm_campaigns  || [];
  const busiestHours   = data?.busiest_hours  || [];
  const revenuePeriod  = data?.revenue_period || 0;

  const referrersClean = useMemo(() => (data?.referrers || []).map(r => ({
    ...r,
    host: (() => {
      try { return new URL(r.referrer).hostname; } catch { return r.referrer || "—"; }
    })(),
  })), [data]);
  const entryPages = data?.entry_pages || [];
  const heatmap    = data?.heatmap     || [];
  const live       = data?.live_visitors_5min || 0;
  const newVis     = data?.new_visitors       || 0;
  const retVis     = data?.returning_visitors || 0;
  const bounce     = data?.bounce_rate        || 0;
  const pps        = data?.pages_per_session  || 0;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">Analytics</span>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl text-[#fdfbf7]">Desempenho da App</h1>
          <p className="mt-2 text-sm text-[#a0b2af]">
            Quem visita, o que clica, onde converte e quanto rende.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-end">
          <button
            onClick={() => setExcludeInternal(v => !v)}
            data-testid="toggle-internal"
            title={excludeInternal
              ? "Tráfego de admin / funcionários está excluído. Clica para incluir."
              : "Tráfego de admin / funcionários está incluído. Clica para excluir."}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] uppercase tracking-wider transition ${
              excludeInternal
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
                : "border-amber-400/30 bg-amber-400/10 text-amber-200"
            }`}>
            <Filter size={12} />
            {excludeInternal ? "Excluindo equipa" : "Incluindo equipa"}
          </button>
          <div className="inline-flex rounded-lg border border-[#1a4640] bg-[#062c27] p-1">
            {RANGES.map(r => (
              <button key={r.d}
                      onClick={() => setDays(r.d)}
                      data-testid={`analytics-range-${r.d}`}
                      className={`px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider transition ${
                        days === r.d
                          ? "bg-[#d4af37] text-[#041f1b]"
                          : "text-[#a0b2af] hover:text-[#fdfbf7]"
                      }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-28 rounded-xl border border-[#1a4640] bg-[#062c27] animate-pulse" />
          ))}
        </div>
      ) : data && (
        <>
          {/* Facturalusa auto-emission toggle (paused?) */}
          {settings && (
            <div
              className={`rounded-xl border p-4 flex items-center justify-between gap-4 flex-wrap ${
                settings.facturalusa_paused
                  ? "border-amber-400/40 bg-amber-400/10"
                  : "border-emerald-400/30 bg-emerald-400/5"
              }`}
              data-testid="facturalusa-toggle-card"
            >
              <div className="flex items-start gap-3">
                {settings.facturalusa_paused
                  ? <PauseCircle className="text-amber-300 mt-0.5" size={20} />
                  : <PlayCircle  className="text-emerald-300 mt-0.5" size={20} />}
                <div>
                  <div className="text-[#fdfbf7] text-sm font-medium">
                    Emissão automática de Fatura-Recibo · {" "}
                    <span className={settings.facturalusa_paused ? "text-amber-300" : "text-emerald-300"}>
                      {settings.facturalusa_paused ? "PAUSADA" : "ATIVA"}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#a0b2af] mt-0.5">
                    {settings.facturalusa_paused
                      ? "Quando marcar uma reserva como paga, a fatura NÃO é enviada automaticamente para a Facturalusa."
                      : "Cada reserva marcada como paga emite automaticamente uma Fatura-Recibo na Facturalusa."}
                  </div>
                  {settings.facturalusa_paused && settings.facturalusa_paused_at && (
                    <div className="text-[10px] text-amber-200/80 mt-1 font-mono">
                      Pausado em {new Date(settings.facturalusa_paused_at).toLocaleString("pt-PT")}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={toggleFacturalusa}
                disabled={togglingFL}
                data-testid="toggle-facturalusa"
                className={`rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 ${
                  settings.facturalusa_paused
                    ? "bg-emerald-400 hover:bg-emerald-300 text-[#041f1b]"
                    : "bg-amber-400 hover:bg-amber-300 text-[#041f1b]"
                }`}>
                {togglingFL
                  ? "..."
                  : settings.facturalusa_paused
                    ? "Ativar Facturalusa"
                    : "Desativar Facturalusa"}
              </button>
            </div>
          )}

          {/* KPIs with period-over-period delta */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Users}             label="Visitantes únicos" value={t.unique_visitors} delta={d.unique_visitors} testId="kpi-visitors" />
            <Kpi icon={Eye}               label="Page views"        value={t.pageviews}       delta={d.pageviews}       testId="kpi-pageviews" />
            <Kpi icon={MousePointerClick} label="Eventos"            value={t.events}          delta={d.events}          testId="kpi-events" />
            <Kpi icon={Wallet}            label="Receita no período" value={fmtEur(revenuePeriod)} testId="kpi-revenue"
                 hint={`${data.range_days} dias · só pagas`} />
          </div>

          {/* Conversion rates */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Kpi icon={TrendingUp} label="Visitante → reserva" value={`${c.visitor_to_booking ?? 0}`} suffix="%" testId="kpi-conv-visitor-booking" />
            <Kpi icon={TrendingUp} label="Visitante → pago"    value={`${c.visitor_to_paid ?? 0}`}    suffix="%" testId="kpi-conv-visitor-paid" />
            <Kpi icon={TrendingUp} label="Reserva → paga"      value={`${c.booking_to_paid ?? 0}`}    suffix="%" testId="kpi-conv-booking-paid" />
          </div>

          {/* Engagement KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi icon={Radio}     label="A navegar agora"  value={live}      hint="últimos 5 min" testId="kpi-live" />
            <Kpi icon={UserPlus}  label="Novos visitantes" value={newVis}    testId="kpi-new-visitors" />
            <Kpi icon={UserCheck} label="Recorrentes"      value={retVis}    testId="kpi-returning" />
            <Kpi icon={Repeat}    label="Páginas / sessão" value={`${pps}`}  testId="kpi-pps" />
            <Kpi icon={LogIn}     label="Taxa de bounce"   value={`${bounce}`} suffix="%"
                 hint="sessão só com 1 página" testId="kpi-bounce" />
          </div>

          {/* Time series + Funnel side by side */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-xl border border-[#1a4640] bg-[#062c27] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm text-[#fdfbf7]">Atividade ao longo do tempo</h3>
                <div className="text-[10px] text-[#7c8e8b]">{timeseries.length} dia(s)</div>
              </div>
              <Sparkline data={timeseries} height={120} />
              <div className="mt-2 flex items-center justify-between text-[10px] text-[#7c8e8b]">
                {timeseries[0] && <span>{timeseries[0].date}</span>}
                {timeseries.at(-1) && <span>{timeseries.at(-1).date}</span>}
              </div>
            </div>
            <Funnel steps={funnel} />
          </div>

          {/* UTM blocks */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ListBlock icon={Tag} title="Top UTM source" testId="utm-sources"
                       items={utmSources}
                       getKey={x => x.key} getValue={x => x.count}
                       tone="gold"
                       empty="Adiciona ?utm_source=google nos teus links para começar." />
            <ListBlock icon={Megaphone} title="Top UTM campaign" testId="utm-campaigns"
                       items={utmCampaigns}
                       getKey={x => x.key} getValue={x => x.count}
                       tone="emerald"
                       empty="Sem campanhas etiquetadas no período." />
            <ListBlock icon={ArrowDownRight} title="UTM medium" testId="utm-mediums"
                       items={utmMediums}
                       getKey={x => x.key} getValue={x => x.count}
                       tone="blue"
                       empty="—" />
          </div>

          {/* Top pages + Top events + Entry pages */}
          <div className="grid lg:grid-cols-3 gap-4">
            <ListBlock icon={Eye} title="Top páginas" testId="top-pages"
                       items={topPages.slice(0, 12)}
                       getKey={p => p.path}
                       getValue={p => p.pageviews}
                       tone="gold" />
            <ListBlock icon={LogIn} title="Páginas de entrada" testId="entry-pages"
                       items={entryPages}
                       getKey={p => p.path}
                       getValue={p => p.count}
                       tone="emerald"
                       empty="Sem sessões registadas." />
            <ListBlock icon={MousePointerClick} title="Top eventos / cliques" testId="top-events"
                       items={topEvents}
                       getKey={e => e.name}
                       getValue={e => e.count}
                       tone="blue" />
          </div>

          {/* Heatmap */}
          <Heatmap data={heatmap} />

          {/* Devices + Browsers + Countries */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ListBlock icon={Smartphone} title="Dispositivos" testId="devices"
                       items={devices} getKey={x => x.key} getValue={x => x.count} tone="blue" />
            <ListBlock icon={Monitor} title="Browsers" testId="browsers"
                       items={browsers} getKey={x => x.key} getValue={x => x.count} tone="gold" />
            <ListBlock icon={Globe} title="Países" testId="countries"
                       items={countries} getKey={x => x.key} getValue={x => x.count} tone="emerald" />
          </div>

          {/* Referrers + busiest hours + by_role */}
          <div className="grid lg:grid-cols-3 gap-4">
            <ListBlock icon={ArrowDownRight} title="Origens (Referrers)" testId="referrers"
                       items={referrersClean}
                       getKey={r => r.host}
                       getValue={r => r.count}
                       tone="gold" />
            <ListBlock icon={Clock} title="Horas mais ativas" testId="busiest-hours"
                       items={[...busiestHours].sort((a, b) => b.pageviews - a.pageviews).slice(0, 8)}
                       getKey={h => `${String(h.hour).padStart(2, "0")}:00 UTC`}
                       getValue={h => h.pageviews}
                       tone="blue" />
            <ListBlock icon={Activity} title="Por papel de utilizador" testId="by-role"
                       items={byRole}
                       getKey={x => x.key === "—" ? "Anónimo / visitante" : x.key}
                       getValue={x => x.count}
                       tone="emerald" />
          </div>
        </>
      )}
    </div>
  );
}
